<template>
  <div class="m-page">
    <van-dropdown-menu class="filter-menu">
      <van-dropdown-item v-model="prosperityFilter" :options="prosperityOptions" />
      <van-dropdown-item ref="themeItem" :title="themeMenuTitle" @open="onThemePanelOpen">
        <div class="theme-panel">
          <van-radio-group v-model="themeDraftMode" direction="horizontal" class="theme-mode">
            <van-radio name="exclude">不含题材</van-radio>
            <van-radio name="include">仅含题材</van-radio>
          </van-radio-group>
          <div v-if="themeOptionGroups.length" class="theme-list">
            <van-checkbox-group v-model="themeDraftThemes">
              <div v-for="g in themeOptionGroups" :key="g.category" class="theme-group">
                <div class="theme-group-label">{{ g.category }}</div>
                <van-checkbox
                  v-for="o in g.options"
                  :key="o.id"
                  :name="o.id"
                  shape="square"
                  class="theme-check"
                >
                  {{ o.name }}（{{ o.count }}）
                </van-checkbox>
              </div>
            </van-checkbox-group>
          </div>
          <van-empty
            v-else
            image-size="48"
            description="暂无题材标签"
            class="theme-empty"
          />
          <div class="theme-actions">
            <van-button size="small" @click="resetThemeFilter">重置</van-button>
            <van-button size="small" type="info" @click="applyThemeFilter">确定</van-button>
          </div>
        </div>
      </van-dropdown-item>
    </van-dropdown-menu>

    <div class="toolbar">
      <van-button size="small" type="info" :loading="loading" icon="replay" @click="loadData">
        刷新
      </van-button>
      <span v-if="summary" class="tip">
        {{ filterActive ? `${filteredItems.length} / ${summary.count} 只` : `推荐 ${summary.count} 只` }}
        · {{ summary.latestDate }}
      </span>
      <van-tag v-if="filterActive" type="warning" size="medium" plain class="clear-tag" @click="clearFilters">
        清除筛选
      </van-tag>
    </div>

    <van-notice-bar
      v-if="summary"
      wrapable
      :scrollable="false"
      background="#ecf9ff"
      color="#1989fa"
      :text="thresholdText"
    />

    <van-empty v-if="error" :description="error" />
    <van-loading v-else-if="loading && !items.length" class="page-loading" vertical>计算中...</van-loading>
    <van-empty
      v-else-if="queried && !filteredItems.length"
      :description="filterActive ? '当前筛选条件下没有推荐股票' : '暂无满足条件的推荐'"
    />

    <van-list v-else finished finished-text="已全部加载">
      <div v-for="row in filteredItems" :key="row.code" class="card">
        <div class="card-head">
          <span class="rank">#{{ row.rank }}</span>
          <a class="code" :href="stockstarCodeLink(row.code)" target="_blank" rel="noopener noreferrer">{{ row.code }}</a>
          <a class="name" :href="eastmoneyCodeLink(row)" target="_blank" rel="noopener noreferrer">{{ row.name }}</a>
          <span class="score">{{ formatScore(row.score) }}</span>
        </div>
        <div class="card-meta">
          <span>{{ row.industry || '—' }}</span>
          <span>{{ row.action || '—' }}</span>
          <span>{{ row.shares }} 家</span>
          <span>{{ formatNum(row.marketValue) }} 亿</span>
        </div>
        <div class="card-row">
          <span>基期 {{ formatPrice(row.priceAsOf) }}</span>
          <span>最新 {{ formatPrice(row.priceLatest) }}</span>
          <span :class="row.declinePct >= 0 ? 'up' : 'down'">
            {{ formatPct(row.declinePct) }}%
          </span>
        </div>
        <div v-if="row.prosperity" class="card-row prosp-row">
          <span
            v-for="m in prosperityMeta"
            :key="m.key"
            class="prosp-tag"
            :class="prospTagClass(row, m.key)"
          >{{ m.short }}{{ prospTagText(row, m.key) }}</span>
        </div>
        <div v-if="row.themes && row.themes.length" class="card-row theme-row">
          <span
            v-for="t in row.themes.slice(0, 3)"
            :key="t.themeId"
            class="theme-chip"
            :class="{ 'theme-chip--hit': themeFilterThemes.includes(t.themeId) }"
            @click="toggleExcludeTheme(t.themeId)"
          >{{ t.themeName }}</span>
          <span v-if="row.themes.length > 3" class="theme-more">+{{ row.themes.length - 3 }}</span>
        </div>
      </div>
    </van-list>
  </div>
</template>

<script>
import { fetchWithCredentials } from '../utils/http.js'
import { eastmoneyCodeLink, stockstarCodeLink } from '../utils/stockLink.js'

const PROSPERITY_META = [
  { key: 'npYoy', short: '成长' },
  { key: 'npAccel', short: '加速' },
  { key: 'cfq', short: '现金流' },
]
const PROSPERITY_KEYS = PROSPERITY_META.map((m) => m.key)
const TAG_CN = { positive: '↑', negative: '↓', neutral: '—' }

export default {
  name: 'MobileRecommend',
  data() {
    return {
      items: [],
      summary: null,
      loading: false,
      queried: false,
      error: '',
      // 景气度筛选（与 PC 端 SystemRecommend 对齐，临时态不落库）
      prosperityFilter: 'all',
      prosperityOptions: [
        { text: '景气度：全部', value: 'all' },
        { text: '剔除任一负向', value: 'exclude-negative' },
        { text: '仅三项全正向', value: 'positive-only' },
      ],
      // 题材筛选：dropdown 面板里先改草稿，点确定才生效
      themeFilterMode: 'exclude',
      themeFilterThemes: [],
      themeDraftMode: 'exclude',
      themeDraftThemes: [],
      themeOptions: [],
      prosperityMeta: PROSPERITY_META,
    }
  },
  computed: {
    themeOptionGroups() {
      const groups = []
      const index = new Map()
      for (const o of this.themeOptions) {
        const cat = o.category || '未分组'
        if (!index.has(cat)) {
          index.set(cat, groups.length)
          groups.push({ category: cat, options: [] })
        }
        groups[index.get(cat)].options.push(o)
      }
      return groups
    },
    themeMenuTitle() {
      if (!this.themeFilterThemes.length) return '题材'
      const verb = this.themeFilterMode === 'exclude' ? '排除' : '仅含'
      return `题材：${verb}${this.themeFilterThemes.length}项`
    },
    filterActive() {
      return this.prosperityFilter !== 'all' || this.themeFilterThemes.length > 0
    },
    filteredItems() {
      let list = this.items
      if (this.prosperityFilter !== 'all' && list.length) {
        if (this.prosperityFilter === 'exclude-negative') {
          // 负面清单：任一指标负向即剔除（无标签的保留，不误伤）
          list = list.filter((it) => {
            const p = it.prosperity
            if (!p) return true
            return !PROSPERITY_KEYS.some((k) => p[k] && p[k].tag === 'negative')
          })
        } else {
          // positive-only：三项全部正向
          list = list.filter((it) => {
            const p = it.prosperity
            return p && PROSPERITY_KEYS.every((k) => p[k] && p[k].tag === 'positive')
          })
        }
      }
      if (this.themeFilterThemes.length && list.length) {
        const set = new Set(this.themeFilterThemes)
        list = list.filter((it) => {
          const hit = (it.themes || []).some((t) => set.has(t.themeId))
          return this.themeFilterMode === 'exclude' ? !hit : hit
        })
      }
      return list
    },
    thresholdText() {
      const t = this.summary?.thresholds
      if (!t) return ''
      return `门槛：市值≥${t.marketValueP}亿 · 家数≥${t.sharesP} · 跌幅<0 · 权重 跌幅${Math.round(t.weights.decline * 100)}%/市值${Math.round(t.weights.marketValue * 100)}%/家数${Math.round(t.weights.shares * 100)}%`
    },
  },
  async created() {
    await this.loadData()
  },
  methods: {
    eastmoneyCodeLink,
    stockstarCodeLink,
    async loadData() {
      this.loading = true
      this.error = ''
      this.queried = true
      try {
        const { res, data } = await fetchWithCredentials('/api/system-recommend')
        if (!res.ok) throw new Error(data.error || `推荐接口失败 ${res.status}`)
        this.summary = {
          count: data.count,
          latestDate: data.latestDate,
          thresholds: data.thresholds,
        }
        this.items = data.items || []
        this.themeOptions = data.themeOptions || []
        // 选项变化后清掉失效的已选题材，避免筛选静默失效
        if (this.themeFilterThemes.length) {
          const valid = new Set(this.themeOptions.map((o) => o.id))
          this.themeFilterThemes = this.themeFilterThemes.filter((id) => valid.has(id))
          this.themeDraftThemes = this.themeFilterThemes.slice()
        }
      } catch (e) {
        this.items = []
        this.summary = null
        this.themeOptions = []
        this.error = e.message || String(e)
      } finally {
        this.loading = false
      }
    },
    /** dropdown 打开时同步草稿值（取消不生效，与 Vant 交互习惯一致） */
    onThemePanelOpen() {
      this.themeDraftMode = this.themeFilterMode
      this.themeDraftThemes = this.themeFilterThemes.slice()
    },
    applyThemeFilter() {
      this.themeFilterMode = this.themeDraftMode
      this.themeFilterThemes = this.themeDraftThemes.slice()
      this.$refs.themeItem.toggle(false)
    },
    resetThemeFilter() {
      this.themeDraftMode = 'exclude'
      this.themeDraftThemes = []
      this.themeFilterMode = 'exclude'
      this.themeFilterThemes = []
      this.$refs.themeItem.toggle(false)
    },
    clearFilters() {
      this.prosperityFilter = 'all'
      this.themeFilterMode = 'exclude'
      this.themeFilterThemes = []
      this.themeDraftMode = 'exclude'
      this.themeDraftThemes = []
    },
    /** 点卡片里的题材标签：直接加入排除列表（再点一次取消），与 PC 端一致 */
    toggleExcludeTheme(themeId) {
      this.themeFilterMode = 'exclude'
      if (this.themeFilterThemes.includes(themeId)) {
        this.themeFilterThemes = this.themeFilterThemes.filter((id) => id !== themeId)
      } else {
        this.themeFilterThemes = this.themeFilterThemes.concat(themeId)
      }
    },
    prospTagOf(row, key) {
      return (row.prosperity && row.prosperity[key]) || null
    },
    prospTagClass(row, key) {
      const t = this.prospTagOf(row, key)
      return [`prosp-tag--${t ? t.tag : 'none'}`]
    },
    prospTagText(row, key) {
      const t = this.prospTagOf(row, key)
      return t ? (TAG_CN[t.tag] || '') : ''
    },
    formatPrice(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    formatPct(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    formatNum(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
    formatScore(v) {
      const n = Number(v)
      return Number.isFinite(n) ? n.toFixed(2) : '—'
    },
  },
}
</script>

<style scoped>
.m-page {
  padding-bottom: 8px;
}

.filter-menu {
  position: sticky;
  top: 0;
  z-index: 2;
}

.toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
}

.tip {
  font-size: 12px;
  color: #969799;
}

.clear-tag {
  cursor: pointer;
}

.page-loading {
  padding: 48px 0;
}

.card {
  margin: 8px 12px;
  padding: 12px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.card-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.rank {
  font-size: 12px;
  color: #969799;
  min-width: 28px;
}

.code,
.name {
  color: #1989fa;
  text-decoration: none;
  font-size: 14px;
}

.name {
  font-weight: 600;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.score {
  font-weight: 700;
  color: #323233;
}

.card-meta,
.card-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px 12px;
  font-size: 12px;
  color: #646566;
  margin-top: 4px;
}

.up { color: #ee0a24; }
.down { color: #07c160; }

/* 景气度行：三个小标签，红=正向 绿=负向 灰=中性/无 */
.prosp-row {
  gap: 6px;
}

.prosp-tag {
  display: inline-block;
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 3px;
  border: 1px solid #e9e9eb;
  background: #fafafa;
  color: #c0c4cc;
}

.prosp-tag--positive {
  color: #ee0a24;
  border-color: #fbc4c4;
  background: #fff1f1;
}

.prosp-tag--negative {
  color: #07c160;
  border-color: #b3e19d;
  background: #f0fff4;
}

.prosp-tag--neutral {
  color: #969799;
  border-color: #d3d4d6;
  background: #f4f4f5;
}

/* 题材行：chips，点一下即按该题材排除（与 PC 端一致） */
.theme-row {
  gap: 6px;
}

.theme-chip {
  display: inline-block;
  max-width: 96px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 11px;
  line-height: 18px;
  padding: 0 6px;
  border-radius: 3px;
  color: #4f63b0;
  border: 1px solid #c9d5f2;
  background: #f2f5ff;
}

.theme-chip--hit {
  color: #b8850f;
  border-color: #f0d9a8;
  background: #fdf6ec;
}

.theme-more {
  font-size: 11px;
  color: #969799;
}

/* 题材筛选面板 */
.theme-panel {
  padding: 12px 16px 8px;
  text-align: left;
}

.theme-mode {
  margin-bottom: 8px;
}

.theme-list {
  max-height: 320px;
  overflow-y: auto;
}

.theme-group {
  margin-bottom: 8px;
}

.theme-group-label {
  font-size: 12px;
  color: #969799;
  margin: 6px 0 4px;
}

.theme-check {
  padding: 3px 0;
  font-size: 13px;
}

.theme-empty {
  padding: 12px 0;
}

.theme-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 8px 0 4px;
}
</style>
